import React, { useState, useRef, useEffect } from 'react';
import './ProfileHeader.css';
import { FaEdit, FaMapMarkerAlt, FaBriefcase, FaCamera, FaSave, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useChatDock } from '../../../contexts/ChatDockContext';
import UserSafetyMenu from '../../../components/UserSafetyMenu';

const ProfileHeader = ({
  profile,
  user,
  supabase,
  onProfileUpdate,
  showNotification,
  isOwnProfile,
  openMessenger
}) => {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(profile.display_name || '');
  const [avatarPreview, setAvatarPreview] = useState(profile.avatar_url || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const { openChat } = useChatDock();

  // Connection status: 'none' | 'pending' | 'accepted'
  const [connectionStatus, setConnectionStatus] = useState('none');
  const [checkingConn, setCheckingConn] = useState(false);

  // Load real connection status between current user and viewed profile
  useEffect(() => {
    let ignore = false;
    async function fetchStatus() {
      if (!user?.id || !profile?.id || user.id === profile.id) {
        setConnectionStatus('none');
        return;
      }
      setCheckingConn(true);
      try {
        const [{ data: out }, { data: inc }] = await Promise.all([
          supabase.from('connections').select('status').eq('user_id', user.id).eq('connection_id', profile.id).limit(1),
          supabase.from('connections').select('status').eq('user_id', profile.id).eq('connection_id', user.id).limit(1)
        ]);
        const rows = [...(out || []), ...(inc || [])];
        let status = 'none';
        if (rows.some(r => String(r.status).toLowerCase() === 'accepted')) status = 'accepted';
        else if (rows.some(r => String(r.status).toLowerCase() === 'pending')) status = 'pending';
        if (!ignore) setConnectionStatus(status);
      } catch {
        if (!ignore) setConnectionStatus('none');
      } finally {
        if (!ignore) setCheckingConn(false);
      }
    }
    fetchStatus();
    return () => { ignore = true; };
  }, [user?.id, profile?.id, supabase]);

  const handleSendConnectionRequest = async () => {
    if (!user?.id || !profile?.id || user.id === profile.id) return;
    try {
      // Check if a row already exists in either direction
      const [{ data: out }, { data: inc }] = await Promise.all([
        supabase.from('connections').select('id,status').eq('user_id', user.id).eq('connection_id', profile.id).limit(1),
        supabase.from('connections').select('id,status').eq('user_id', profile.id).eq('connection_id', user.id).limit(1)
      ]);
      const existing = ([...(out || []), ...(inc || [])][0]) || null;
      if (existing) {
        // If existing is rejected or pending, update to pending if needed (only if we are the requester)
        if (!out || out.length === 0) {
          // There is an inbound row; don't mutate it here. Just reflect UI.
          setConnectionStatus(String(existing.status).toLowerCase() === 'accepted' ? 'accepted' : 'pending');
          showNotification && showNotification('Request already exists.', 'success');
          return;
        }
        // We have an outbound row already; reflect its status
        setConnectionStatus(String(existing.status).toLowerCase() === 'accepted' ? 'accepted' : 'pending');
        showNotification && showNotification('Request already sent.', 'success');
        return;
      }
      // Insert a new pending request
      const { error } = await supabase
        .from('connections')
        .insert({ user_id: user.id, connection_id: profile.id, status: 'pending' });
      if (!error) {
        setConnectionStatus('pending');
        showNotification && showNotification('Connection request sent!', 'success');
      }
    } catch (e) {
      showNotification && showNotification('Failed to send request.', 'error');
    }
  };

  const handleEditClick = () => setEditing(true);

  const handleCancel = () => {
    setEditing(false);
    setDisplayName(profile.display_name || '');
    setAvatarPreview(profile.avatar_url || '');
    setAvatarFile(null);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showNotification && showNotification('Only JPG, PNG, GIF, or WEBP images are allowed.', 'error');
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    let avatarUrl = profile.avatar_url;
    try {
      if (avatarFile) {
        const fileExt = avatarFile.name.split('.').pop();
        const fileName = `${user.id}_${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, avatarFile);
        if (!uploadError) {
          const { data: publicUrlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);
          avatarUrl = publicUrlData.publicUrl;
        } else {
          throw new Error('Failed to upload avatar.');
        }
      }
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          display_name: displayName,
          avatar_url: avatarUrl,
        })
        .eq('id', user.id);
      if (!updateError) {
        onProfileUpdate && onProfileUpdate({
          ...profile,
          display_name: displayName,
          avatar_url: avatarUrl,
        });
        setEditing(false);
        showNotification && showNotification('Profile updated successfully!', 'success');
        // Navigate to the refreshed profile page
        navigate(`/profile/${user.id}`);
      } else {
        throw new Error('Failed to update profile.');
      }
    } catch (err) {
      showNotification && showNotification(err.message || 'Error updating profile.', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="profile-header">
      <div className="profile-header-main-row">
        <div className="profile-avatar">
          {editing ? (
            <>
              <label htmlFor="avatar-upload" className="avatar-upload-label">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar preview" />
                ) : (
                  <div className="avatar-placeholder">
                    {(displayName || profile.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="avatar-upload-icon">
                  <FaCamera />
                </div>
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleAvatarChange}
              />
            </>
          ) : profile.avatar_url ? (
            <img src={profile.avatar_url} alt={`${profile.display_name || profile.name}'s avatar`} />
          ) : (
            <div className="avatar-placeholder">
              {(profile.display_name || profile.name || 'U').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="profile-info-group">
          <div className="profile-info-main">
            {editing ? (
              <input
                className="display-name-input"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                maxLength={32}
                disabled={saving}
              />
            ) : (
              <h1>{profile.display_name || 'User'}</h1>
            )}
            {profile.name && <p className="profile-real-name">{profile.name}</p>}
          </div>
          <div className="profile-info-details">
            <div className="profile-info-details">
              <p className="profile-location">
                <FaMapMarkerAlt style={{ marginRight: '5px' }} /> {profile.location || "Location not set"}
              </p>
              {profile.occupation && (
                <p className="profile-occupation">
                  <FaBriefcase style={{ marginRight: '5px' }} /> {profile.occupation}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {editing ? (
        <div className="profile-header-actions">
          <button className="save-profile-button" onClick={handleSave} disabled={saving}>
            <FaSave /> Save
          </button>
          <button className="cancel-profile-button" onClick={handleCancel} disabled={saving}>
            <FaTimes /> Cancel
          </button>
        </div>
      ) : (
        isOwnProfile && (
          <button className="edit-profile-button" onClick={handleEditClick} title="Edit Header">
            <FaEdit /><span className="btn-text"> Edit Header</span>
          </button>
        )
      )}
      {!isOwnProfile && (
        <div className="profile-action-row">
          {connectionStatus === 'none' && (
            <button className="connect-btn" onClick={handleSendConnectionRequest} disabled={checkingConn}>
              Connect
            </button>
          )}
          {connectionStatus === 'pending' && (
            <button className="pending-btn" disabled>
              Request Sent
            </button>
          )}
          {connectionStatus === 'accepted' && (
            <>
              <button
                className="message-btn"
                onClick={() => openChat(profile.id)}
                title="Open Messenger"
              >
                Message
              </button>
              <button
                className="remove-connection-btn"
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    await supabase
                      .from('connections')
                      .delete()
                      .or(`and(user_id.eq.${user.id},connection_id.eq.${profile.id}),and(user_id.eq.${profile.id},connection_id.eq.${user.id})`);
                    setConnectionStatus('none');
                    showNotification && showNotification('Disconnected successfully.', 'success');
                  } catch (e) {
                    showNotification && showNotification('Failed to disconnect.', 'error');
                  }
                }}
                title="Disconnect"
              >
                Disconnect
              </button>
            </>
          )}
          {/* Safety menu (Block/Report) */}
          <UserSafetyMenu
            targetUserId={profile.id}
            targetDisplayName={profile.display_name || profile.name}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;